import { faCaretRight } from "@fortawesome/pro-solid-svg-icons/faCaretRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Card, CardActions, CardContent, Checkbox, FormControlLabel, Grid, TextField } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import { Alert } from "@mui/lab";
import { default as Auth, default as AuthService } from "api/Auth";
import styles from "assets/jss/public-styles";
import ButtonClose from "components/Buttons/ButtonClose";
import { RequestIndicator, SpinnerIndicator } from "components/CallServer";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import MuiRegularButton from "components/MuiExtras/Button/MuiRegularButton";
import CardHeaderColor from "components/MuiExtras/Card/CardHeaderColor";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiLink from "components/MuiExtras/MuiLink";
import MuiMessageBlock from "components/MuiExtras/MuiMessageBlock";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import AppMessage from "components/Translations/AppMessage";
import { AccountLoginResult, AccountType } from "models/ApiEnums";
import { AuthenticationResult } from "models/ApiModels";
import * as React from "react";
import { Trans, WithTranslation, withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Link, RouteComponentProps } from "react-router-dom";
import { Action, Dispatch } from "redux";
import { RoutePaths } from "routes/RouteConfig";
import Session from 'services/Session';
import * as GenieActions from "store/genie/actions";
import { AuthHelpers } from "views/Public/AuthHelpers";
import Agreement from "views/System/Components/Agreement";

////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    initializing: boolean;
    firstLoad: boolean;
    lastError: any;
    acceptSaml: boolean;

    keepConnected: boolean;
    id: string;
    pass: string;
    submiting: boolean;
    submitingSaml: boolean;
    agree: boolean;
    authResult: AuthenticationResult;
    authMethod: AccountType;
}

interface IProps {
}

interface IDispatchProps {
    onGenieInit: () => void;
}

type Props = IProps & IDispatchProps & RouteComponentProps<IProps, any, any> & WithStyles<typeof styles> & WithSnackbar & WithTranslation;

////////////////////////////////////////////////////////////////////////////////////////

class LoginPage extends React.Component<Props, IState> {
    private authService = new AuthService();
    private authInputRef: HTMLElement = null;
    private passwordInputRef: HTMLElement = null;

    constructor(props: Props) {
        super(props);

        this.state = {
            initializing: true,
            firstLoad: true,
            submiting: false,
            submitingSaml: false,
            acceptSaml: false,
            lastError: null,

            keepConnected: Session.keepConnected,
            id: null,
            pass: null,
            agree: false,
            authResult: null,
            authMethod: null,
        };
    }

    ///
    private checkAccountLoginMethod() {
        if (this.state.id == null || this.state.id.length == 0 || !this.state.acceptSaml) {
            this.setState({ authMethod: null });
        }
        else {
            this.authService.getAccountType(this.state.id)
                .then((authMethod) => {
                    this.setState({ authMethod }, () => {
                        if ((authMethod & AccountType.Local) > 0)
                            this.passwordInputRef.focus();
                    });
                });
        }
    }

    ///
    private openSamlDialog(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        Auth.openSamlDialog(Session.loginOptions.samlLoginUrl);
    }

    ///
    private onSamlReceived = (event: CustomEvent) => {
        event.preventDefault();
        this.handleSubmitWithSaml(event.detail);
    };

    ///
    private handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (event.keyCode == 13) {
            event.preventDefault();
            this.checkAccountLoginMethod();
        }
    };

    ///
    private handlePasswordKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (event.keyCode == 13) {
            event.preventDefault();
            this.handleSubmit();
        }
    };

    ///
    private handleResendConfirmation = () => {
        this.authService.resendMailConfirmation(this.state.id).then((result) => {
            if (result) this.props.snackbar.showMessage(this.props.t("Views.Public.Login.ResendConfirmationSuccess"), "success");
            else this.props.snackbar.showMessage(this.props.t("Views.Public.Login.ResendConfirmationError"), "error");
        });
    };

    ///
    private handleSubmit() {

        const { id, pass } = this.state;

        if (id?.length == 0 || pass?.length == 0)
            return;

        const returnUrl = this.props.location.state && this.props.location.state.from && this.props.location.state.from.pathname
            ? this.props.location.state.from.pathname
            : RoutePaths.TimelineStartup;

        this.setState({ submiting: true, submitingSaml: false });

        this.authService.signIn(id, pass, this.state.keepConnected)
            .then((response) => {
                if (response.status == AccountLoginResult.Valid) {
                    // Redireciona para a Home ou para o local que solicitou autenticação
                    this.props.history.push(returnUrl);
                }
                else {

                    if (response.status == AccountLoginResult.Failed) {
                        this.props.snackbar.showMessage(<AppMessage resource="AuthenticationFailed" />, "error");
                    }

                    this.authInputRef.focus();

                    this.setState({
                        firstLoad: false,
                        submiting: false,
                        authResult: response,
                    });
                }
            })
            .catch((error: any) => {
                this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error");

                this.setState({
                    firstLoad: false,
                    submiting: false,
                    authResult: null
                }, () => {
                    this.authInputRef.focus();
                });
            });
    }

    ///
    private handleSubmitWithSaml(samlResponse: string) {

        if (samlResponse == null || samlResponse.length == 0)
            return;

        const returnUrl = this.props.location.state && this.props.location.state.from && this.props.location.state.from.pathname
            ? this.props.location.state.from.pathname
            : RoutePaths.TimelineStartup;

        this.setState({ submiting: false, submitingSaml: true });

        this.authService.signInWithSaml(samlResponse, this.state.keepConnected)
            .then((response) => {
                if (response.status == AccountLoginResult.Valid) {
                    // Redireciona para a Home ou para o local que solicitou autenticação
                    this.props.history.push(returnUrl);
                }
                else {
                    if (response.status == AccountLoginResult.Failed) {
                        this.props.snackbar.showMessage(<AppMessage resource="AuthenticationFailed" />, "error");
                    }

                    this.authInputRef.focus();

                    this.setState({
                        firstLoad: false,
                        submitingSaml: false,
                        authResult: response,
                    });
                }
            })
            .catch((error: any) => {
                this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error");

                this.setState({
                    firstLoad: false,
                    submitingSaml: false,
                    authResult: null
                }, () => {
                    this.authInputRef.focus();
                });
            });
    }

    ///
    private confirmAgreement() {

        const { authResult, keepConnected } = this.state;

        const returnUrl = this.props.location.state && this.props.location.state.from && this.props.location.state.from.pathname
            ? this.props.location.state.from.pathname
            : RoutePaths.TimelineStartup;

        this.setState({ submiting: true });

        this.authService.confirmAgreement(authResult.continueAuthStamp, keepConnected)
            .then((response) => {
                if (response == AccountLoginResult.Valid) {
                    // Redireciona para a Home ou para o local que solicitou autenticação
                    this.props.history.push(returnUrl);
                }
                else {
                    this.setState({ submiting: false });

                    if (response == AccountLoginResult.SecurityStampExpired)
                        this.props.snackbar.showMessage(<AppMessage resource="AgreementStampExpires" />, "warning");
                    else
                        this.props.snackbar.showMessage(<AppMessage resource="AgreementSignFailed" />, "error");
                }
            })
            .catch((error: any) => {
                this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error");
                this.setState({ submiting: false });
            });
    }

    ///
    private renderLockout() {
        return (
            <MuiMessageBox
                variant="error"
                message={
                    <Trans
                        i18nKey="Views.Public.Login.LockoutMessage"
                        components={
                            [
                                <p></p>,
                            ]
                        }
                    />
                }
                action={<ButtonClose color="error" onClick={() => this.setState({ authResult: null })} />}
            />
        );
    }

    ///
    private renderAccountExpired() {
        return (
            <MuiMessageBox
                variant="error"
                message={
                    <Trans
                        i18nKey="Views.Public.Login.AccountExpiredMessage"
                        components={
                            [
                                <b></b>,
                                <p></p>,
                            ]
                        }
                    />
                }
                action={<ButtonClose color="error" onClick={() => this.setState({ authResult: null })} />}
            />
        );
    }

    ///
    private renderNotConfirmed() {
        return (
            <MuiMessageBox
                variant="warning"
                message={
                    <>
                        <Trans
                            i18nKey="Views.Public.Login.NotConfirmedAccountMessage"
                            components={
                                [
                                    <p></p>,
                                    <i></i>,
                                ]
                            }
                        />
                        <p>
                            <Trans
                                i18nKey="Views.Public.Login.NotConfirmedAccountLink"
                                components={
                                    [
                                        <MuiLink color="inherit" onClick={this.handleResendConfirmation}>{this.props.t("Common.Literals.ClickHereText")}</MuiLink>
                                    ]
                                }
                            />
                        </p>
                    </>
                }
                action={<ButtonClose color="warning" onClick={() => this.setState({ authResult: null })} />}
            />
        );
    }

    ///
    private renderNotAgreement() {
        const { authResult } = this.state;

        return (
            <Card>
                <CardHeaderColor color="warning" title={this.props.t("Views.Public.Login.AgreementTitle")} />

                <CardContent>
                    <div>
                        {
                            authResult.status == AccountLoginResult.NotAgreement
                                ? <Alert severity="warning">{this.props.t("Views.Public.Login.NotAgreementWarning")}</Alert>
                                : <Alert severity="info">{this.props.t("Views.Public.Login.NotAgreementInfo")}</Alert>
                        }
                        <Agreement onChange={(checked) => this.setState({ agree: checked })} />
                    </div>
                </CardContent>
                <CardActions className={this.props.classes.cardFooter}>
                    <Button
                        color="primary"
                        size="large"
                        variant="contained"
                        component="span"
                        disabled={!this.state.agree || this.state.submiting}
                        onClick={() => this.confirmAgreement()}
                    >
                        <span>{this.props.t("Common.Buttons.ConcludeText")}</span>
                        {this.state.submiting && <SpinnerIndicator />}
                    </Button>
                </CardActions>
            </Card>
        );
    }

    ///
    private renderTopMessage() {

        const { firstLoad } = this.state;
        const search = this.props.location.search;
        const params = new URLSearchParams(search);

        if (firstLoad) {

            if (params.get('confirmed')) {
                return <MuiMessageBlock center variant="success">{this.props.t("Views.Public.Login.ConfirmedMessage")}</MuiMessageBlock>;
            }

            if (params.get('expired')) {
                return (
                    <MuiMessageBlock center variant="warning">
                        <Trans
                            i18nKey="Views.Public.Login.ExpiredMessage"
                            components={[<b></b>]}
                        />
                    </MuiMessageBlock>
                );
            }

            if (params.get('agree')) {
                return (
                    <MuiMessageBlock center variant="warning">
                        <Trans
                            i18nKey="Views.Public.Login.NotAgreementWarning"
                            components={[<b></b>]}
                        />
                    </MuiMessageBlock>
                );
            }

            if (this.props.history.location.state && this.props.history.location.state.signedOut) {
                return <MuiMessageBlock center variant="info"><strong>{this.props.t("Views.Public.Login.LogoutMessage")}</strong></MuiMessageBlock>;

            }
        }

        return null;
    }

    ///
    private isLocalAccount() {
        return this.state.authMethod != null && (this.state.authMethod & AccountType.Local) != 0;
    }

    ///
    private isSamlAccount() {
        return this.state.authMethod != null && (this.state.authMethod & AccountType.Saml) != 0;
    }

    ///
    private renderFooterOptions() {
        const { classes } = this.props;

        if (!Session.loginOptions.authOptions.enablePasswordRecovery && !Session.loginOptions.authOptions.enableAutoRegister)
            return null;

        if (this.state.acceptSaml) {
            return (
                <div className={classes.optionsBlock}>
                    {
                        this.isLocalAccount() && Session.loginOptions.authOptions.enablePasswordRecovery === true &&
                        <div>
                            <FontAwesomeIcon icon={faCaretRight} fixedWidth />
                            <Link to={RoutePaths.ForgotPassword}>{this.props.t("Views.Public.Login.ForgotPasswordText")}</Link>
                        </div>
                    }

                    {
                        this.state.authMethod == null &&
                        <Grid item>
                            <Grid container>
                                {
                                    <Grid item>
                                        <MuiRegularButton
                                            type="button"
                                            color="primary"
                                            size="large"
                                            disabled={this.state.submiting || this.state.submitingSaml}
                                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.openSamlDialog(e)}
                                        >
                                            <span>{this.props.t("Views.Public.Login.EnterWithSml")}</span>
                                            {this.state.submitingSaml && <SpinnerIndicator />}
                                        </MuiRegularButton>
                                    </Grid>
                                }

                                {
                                    Session.loginOptions.authOptions.enableAutoRegister === true &&
                                    <Grid item style={{ marginLeft: "auto" }}>

                                        <MuiRegularButton
                                            type="button"
                                            color="primary"
                                            size="large"
                                            disabled={this.state.submiting || this.state.submitingSaml}
                                            onClick={
                                                () => {
                                                    this.props.history.push(RoutePaths.Register);
                                                }
                                            }
                                        >
                                            <span>{this.props.t("Views.Public.Login.CreateAccountText")}</span>
                                        </MuiRegularButton>
                                    </Grid>
                                }

                            </Grid>
                        </Grid>
                    }
                </div>
            );
        }

        return (
            <Grid item className={classes.optionsBlock}>
                <Grid container>
                    {
                        Session.loginOptions.authOptions.enablePasswordRecovery === true &&
                        <Grid item>
                            <FontAwesomeIcon icon={faCaretRight} fixedWidth />
                            <Link to={RoutePaths.ForgotPassword}>{this.props.t("Views.Public.Login.ForgotPasswordText")}</Link>
                        </Grid>
                    }
                    {
                        Session.loginOptions.authOptions.enableAutoRegister === true &&
                        <Grid item style={{ marginLeft: "auto" }}>
                            <FontAwesomeIcon icon={faCaretRight} fixedWidth />
                            <Link to={RoutePaths.Register}>{this.props.t("Views.Public.Login.CreateAccountText")}</Link>
                        </Grid>
                    }
                </Grid>
            </Grid>
        );
    }

    ///
    private loginForm() {
        const { classes } = this.props;
        const { authResult, keepConnected, submiting, submitingSaml, id, pass, acceptSaml } = this.state;

        const error = authResult == null ? null : <MuiMessageBlock variant="warning">{AuthHelpers.translateStatus(authResult.status)}</MuiMessageBlock>;

        return (
            <>
                <Card>
                    <form autoComplete="on" spellCheck={false}>

                        <CardContent>

                            {error}

                            <TextField
                                id="email"
                                label={this.props.t<string>("Views.Public.Login.EmailLabel")}
                                autoComplete="genieId"
                                autoFocus
                                margin="normal"
                                fullWidth={true}
                                error={id != null && id.length == 0}
                                disabled={submiting || submitingSaml}
                                value={this.state.id || ""}
                                onChange={(e) => { this.setState({ id: e.target.value, submiting: false }); }}
                                onBlur={acceptSaml ? () => this.checkAccountLoginMethod() : null}
                                inputRef={(ref) => this.authInputRef = ref}
                                InputLabelProps={
                                    {
                                        shrink: true
                                    }
                                }
                                inputProps={
                                    {
                                        maxLength: 255,
                                        onKeyDown: this.handleKeyDown
                                    }
                                }
                            />

                            {
                                (!acceptSaml || this.isLocalAccount()) &&
                                <TextField
                                    id="pass"
                                    name="password"
                                    label={this.props.t<string>("Views.Public.Login.PasswordLabel")}
                                    type="password"
                                    autoComplete="geniePass"
                                    error={pass != null && pass.length == 0}
                                    margin="normal"
                                    fullWidth={true}
                                    disabled={submiting || submitingSaml}
                                    value={this.state.pass || ""}
                                    inputRef={(ref) => this.passwordInputRef = ref}
                                    onChange={(e) => { this.setState({ pass: e.target.value, submiting: false }); }}
                                    InputLabelProps={
                                        {
                                            shrink: true
                                        }
                                    }
                                    inputProps={{
                                        maxLength: 32,
                                        onKeyDown: (e) => this.handlePasswordKeyDown(e)
                                    }}
                                />
                            }

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={keepConnected}
                                        onChange={
                                            () => {
                                                this.setState({ keepConnected: !keepConnected });
                                                Session.keepConnected = !keepConnected;
                                            }
                                        }
                                        value=""
                                    />
                                }
                                label={this.props.t<string>("Views.Public.Login.KeepConnectedLabel")}
                                title={this.props.t("Views.Public.Login.KeepConnectedTitle")}
                            />
                        </CardContent>

                        <CardActions className={classes.cardFooter}>
                            {
                                (!acceptSaml || this.isLocalAccount()) &&
                                <MuiRegularButton
                                    type="button"
                                    color="primary"
                                    size="large"
                                    disabled={submiting || submitingSaml || this.state.id == null || this.state.id?.length == 0 || this.state.pass == null || this.state.pass?.length == 0}
                                    onClick={
                                        (e: React.MouseEvent<HTMLButtonElement>) => {
                                            e.preventDefault();
                                            this.handleSubmit();
                                        }
                                    }
                                >
                                    <span>{this.props.t("Views.Public.Login.SubmitText")}&nbsp;</span>
                                    {this.state.submiting && <SpinnerIndicator />}
                                </MuiRegularButton>
                            }

                            {
                                acceptSaml && this.isSamlAccount() &&
                                <MuiRegularButton
                                    type="button"
                                    color="primary"
                                    size="large"
                                    disabled={submiting || submitingSaml}
                                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.openSamlDialog(e)}
                                >
                                    <span>{this.props.t("Views.Public.Login.SubmitWithSml")}</span>
                                    {this.state.submitingSaml && <SpinnerIndicator />}
                                </MuiRegularButton>
                            }

                            {
                                this.state.authMethod == AccountType.Undefined && acceptSaml &&
                                <MuiRegularButton
                                    type="button"
                                    color="primary"
                                    size="large"
                                    disabled={submiting || submitingSaml}
                                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.openSamlDialog(e)}
                                >
                                    <span>{this.props.t("Views.Public.Login.EnterWithSml")}</span>
                                    {this.state.submitingSaml && <SpinnerIndicator />}
                                </MuiRegularButton>
                            }

                            {
                                this.state.authMethod == AccountType.Undefined && Session.loginOptions.authOptions.enableAutoRegister &&
                                <MuiRegularButton
                                    type="button"
                                    color="primary"
                                    size="large"
                                    disabled={this.state.submiting || this.state.submitingSaml}
                                    onClick={
                                        () => {
                                            this.props.history.push(RoutePaths.Register);
                                        }
                                    }
                                >
                                    <span>{this.props.t("Views.Public.Login.CreateAccountText")}</span>
                                </MuiRegularButton>
                            }

                            {
                                acceptSaml && this.state.authMethod == null &&
                                <MuiRegularButton
                                    type="button"
                                    color="primary"
                                    size="large"
                                    disabled={submiting || submitingSaml}
                                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.checkAccountLoginMethod()}
                                >
                                    <span>{this.props.t("Views.Public.Login.NextStepText")}</span>
                                </MuiRegularButton>
                            }


                        </CardActions>
                    </form>
                </Card>

                {this.renderFooterOptions()}
            </>
        );
    }

    ///
    public componentDidMount(): void {

        this.authService
            .getSettings()
            .then((settings) => {
                Session.storeSettings(settings);

                const acceptSaml = Session.loginOptions.samlLoginUrl != null;

                this.setState({
                    initializing: false,
                    acceptSaml: acceptSaml
                });

                setTimeout(() => {
                    if (this.authInputRef != null) this.authInputRef.focus();
                }, 500);

                if (acceptSaml) {
                    document.addEventListener('saml', this.onSamlReceived, false);
                }

                this.props.onGenieInit();
            })
            .catch((error) => {
                this.setState({ lastError: error, initializing: false, acceptSaml: false });
                this.props.onGenieInit();
            });
    }

    ///
    public componentWillUnmount() {
        if (this.state.acceptSaml) {
            document.removeEventListener('saml', this.onSamlReceived, false);
        }
    }

    ///
    public render() {
        const { authResult, initializing, lastError } = this.state;

        if (initializing)
            return <RequestIndicator type="waiting" />;

        if (lastError)
            return <RequestErrorReport error={lastError} />;

        return (
            <>
                {this.renderTopMessage()}

                {
                    authResult != null && authResult.status == AccountLoginResult.Lockedout
                        ? this.renderLockout()
                        : authResult != null && authResult.status == AccountLoginResult.AccountExpired
                            ? this.renderAccountExpired()
                            : authResult != null && authResult.status == AccountLoginResult.NotConfirmed
                                ? this.renderNotConfirmed()
                                : authResult != null && (authResult.status == AccountLoginResult.NotAgreement || authResult.status == AccountLoginResult.AgreementUpdated)
                                    ? this.renderNotAgreement()
                                    : this.loginForm()
                }
            </>
        );
    }
}

function mapDispatchToProps(dispatch: Dispatch<Action<any>>): IDispatchProps {
    return {
        onGenieInit: () => dispatch(GenieActions.init()),
    };
}

export default connect<{}, IDispatchProps>(null, mapDispatchToProps)(withSnackbar(withTranslation()(withStyles(styles)(LoginPage))));

